<template>
  <v-container fluid class="no-print" px-0 pt-0 pb-4>
    <v-row dense justify="end">
      <v-col cols="12" md="auto" :order="1" :order-md="0">
        <v-text-field
          v-if="showSearch"
          v-model="searchValue"
          class="inline-search slim-control"
          :prepend-inner-icon="bmIconName('Search')"
          :placeholder="$m.grid_search"
          color="brand02"
        />
      </v-col>
      <v-col v-if="showExpandAll" cols="auto">
        <bm-btn
          button-type="secondary"
          data-qa="toggle-expand-all"
          @click="emit('toggle-expand-all')"
        >
          <template v-if="allRowsExpanded">
            <bm-icon icon="ArrowUpward" :left="screenSize.smAndUp" />
            {{ $m.grid_collapse_all }}
          </template>
          <template v-else>
            <bm-icon icon="ArrowDownward" :left="screenSize.smAndUp" />
            {{ $m.grid_expand_all }}
          </template>
        </bm-btn>
      </v-col>
      <template v-if="!hideFilter || !hideSort || showManageColumns">
        <v-col cols="auto" class="grid-controls-actions">
          <v-badge
            style="z-index: 1"
            :value="activelyFilteredColumns.length > 0"
            :content="activelyFilteredColumns.length"
            color="primaryChip"
            overlap
          >
            <bm-btn
              v-if="!hideFilter || !hideSort"
              button-type="secondary"
              @click="showFilterDrawer = !showFilterDrawer"
            >
              <bm-icon icon="FilterList" :left="screenSize.smAndUp" />
              {{ screenSize.smAndUp ? sortAndOrFilterText : "" }}
            </bm-btn>
          </v-badge>
          <bm-btn
            v-if="showManageColumns"
            button-type="secondary"
            @click="showColumnSelectorDrawer = !showColumnSelectorDrawer"
          >
            <bm-icon icon="ViewColumn" :left="screenSize.smAndUp" />
            {{ screenSize.smAndUp ? $m.grid_manage_columns : "" }}
          </bm-btn>
          <bm-btn
            v-if="showExport"
            button-type="secondary"
            @click="exportButtonClicked"
          >
            <bm-icon icon="FileDownload" :left="screenSize.smAndUp" />
            {{ screenSize.smAndUp ? $m.grid_export : "" }}
          </bm-btn>
          <slot
            v-if="$slots['additional-controls']"
            name="additional-controls"
          />
        </v-col>
      </template>
    </v-row>
    <!-- Column selector -->
    <v-navigation-drawer
      v-if="showColumnSelectorDrawer"
      v-model="showColumnSelectorDrawer"
      :aria-hidden="!showColumnSelectorDrawer"
      :hidden="!showColumnSelectorDrawer"
      class="grid-drawer manage-columns"
      temporary
      hide-overlay
      right
      app
    >
      <v-list-item :aria-hidden="!showColumnSelectorDrawer">
        <v-list-item-content>
          <v-list-item-title class="bm-headline">
            {{ $m.grid_manage_columns }}
          </v-list-item-title>
        </v-list-item-content>
        <v-list-item-action>
          <v-btn icon>
            <bm-icon icon="Close" @click="showColumnSelectorDrawer = false" />
          </v-btn>
        </v-list-item-action>
      </v-list-item>

      <v-divider></v-divider>

      <v-list-item>
        <v-list-item-content class="py-0">
          <bm-btn
            button-type="ghost"
            :t="$m.keys.grid_clear_all_filters"
            @click="resetAllFilters"
          />
        </v-list-item-content>
      </v-list-item>

      <v-divider></v-divider>

      <v-list-item>
        <v-list-item-content class="d-block">
          <bm-checkbox
            v-for="col in removableColumns"
            :key="col.columnKey"
            :label="col.headingText"
            :disabled="!col.removable"
            :value="!col.removed"
            @update:value="col.remove(col.removed)"
          />
        </v-list-item-content>
      </v-list-item>
    </v-navigation-drawer>
    <!-- Filter selector -->
    <v-navigation-drawer
      v-if="showFilterDrawer"
      v-model="showFilterDrawer"
      :aria-hidden="!showFilterDrawer"
      :hidden="!showFilterDrawer"
      class="grid-drawer"
      temporary
      hide-overlay
      right
      app
    >
      <v-list-item :aria-hidden="!showFilterDrawer">
        <v-list-item-content>
          <v-list-item-title class="bm-headline">
            {{ sortAndOrFilterText }}
          </v-list-item-title>
        </v-list-item-content>
        <v-list-item-action>
          <v-btn icon>
            <bm-icon icon="Close" @click="showFilterDrawer = false" />
          </v-btn>
        </v-list-item-action>
      </v-list-item>

      <v-divider></v-divider>

      <template v-if="activelyFilteredColumns.length > 0">
        <v-list-item>
          <v-list-item-content>
            <v-container fluid pa-0>
              <v-row dense>
                <v-col
                  v-for="[key] in activelyFilteredColumns"
                  :key="key"
                  cols="auto"
                >
                  <bm-chip
                    :text="$t(key).toString()"
                    color="primaryChip"
                    pill
                    small
                    close
                    :close-icon="bmIconName('Close')"
                    @click:close="deleteFilter(key)"
                  />
                </v-col>
              </v-row>
            </v-container>
          </v-list-item-content>
        </v-list-item>

        <v-divider></v-divider>
      </template>

      <v-list-item>
        <v-list-item-content class="py-0">
          <bm-btn
            button-type="ghost"
            :t="$m.keys.grid_clear_all_filters"
            @click="resetAllFilters"
          />
        </v-list-item-content>
      </v-list-item>

      <v-divider></v-divider>

      <v-card
        v-for="col in sortableAndFilterableColumns"
        :key="col.columnKey"
        class="control-box"
        outlined
      >
        <v-card-title
          class="bm-headline clickable"
          style="word-break: break-word"
          @click="col.toggleExpanded()"
        >
          {{ col.headingText }}
          <v-spacer />
          <bm-icon
            :icon="col.expanded ? 'KeyboardArrowUp' : 'KeyboardArrowDown'"
          />
        </v-card-title>
        <v-divider v-if="col.expanded"></v-divider>
        <v-expand-transition>
          <v-card-text v-if="col.expanded" class="sort-controls pb-4">
            <template v-if="!hideSort">
              <bm-checkbox
                block
                width="100%"
                :label="$m.grid_sort_ascending"
                :value="col.sortedAsc"
                @update:value="
                  (val) => (val ? col.sort(false) : col.clearSort())
                "
              />
              <bm-checkbox
                block
                width="100%"
                :label="$m.grid_sort_descending"
                :value="col.sortedDesc"
                @update:value="
                  (val) => (val ? col.sort(true) : col.clearSort())
                "
              />

              <v-divider />
            </template>

            <component
              :is="col.filterComponent"
              v-if="!hideFilter && col.filterComponent"
              :grid-state="gridState"
              :column-config="gridConfiguration.columns[col.columnKey]"
              :column-key="col.columnKey"
            />
          </v-card-text>
        </v-expand-transition>
      </v-card>
    </v-navigation-drawer>
    <!-- Export Draw -->
    <v-navigation-drawer
      v-if="showExportDrawer"
      v-model="showExportDrawer"
      :aria-hidden="!showExportDrawer"
      :hidden="!showExportDrawer"
      class="grid-drawer"
      temporary
      hide-overlay
      right
      app
    >
      <v-list-item :aria-hidden="!showExportDrawer">
        <v-list-item-content>
          <v-list-item-title class="bm-headline">
            {{ $m.grid_export }}
          </v-list-item-title>
        </v-list-item-content>
        <v-list-item-action>
          <v-btn icon>
            <bm-icon icon="Close" @click="showExportDrawer = false" />
          </v-btn>
        </v-list-item-action>
      </v-list-item>

      <v-divider></v-divider>
      <v-list-item>
        <v-list-item-content class="py-0">
          <bm-btn block @click="exportGrid('csv')">CSV</bm-btn>
        </v-list-item-content>
      </v-list-item>
      <v-list-item>
        <v-list-item-content class="py-0">
          <bm-btn block @click="exportGrid('pdf')">PDF</bm-btn>
        </v-list-item-content>
      </v-list-item>
      <v-list-item>
        <v-list-item-content class="py-0">
          <bm-btn block @click="exportGrid('googleSheets')"
            >Google Sheets</bm-btn
          >
        </v-list-item-content>
      </v-list-item>
    </v-navigation-drawer>
  </v-container>
</template>

<script setup lang="ts">
import BmBtn from "@/components/controls/input/BmBtn.vue";
import type {
  BmGridConfiguration,
  BmGridExportEvent,
  BmGridState,
  GridExportFormat,
} from "@/components/controls/grid/BmGridData";
import type { Component as VueComponent, VueConstructor } from "vue";
import { computed, onUnmounted, watch } from "vue";
import { activeGridRegistry } from "@/components/controls/grid/activeGridRegistry";
import { useRouter } from "@/router/router";
import { indexedKeyComparer } from "@/components/controls/grid/BmGridComparers";
import { debounce } from "lodash-es";
import messages, { translate } from "@/plugins/i18n";
import Key from "@/app/types/common/key";
import { hasPermissionsUsingUserStore } from "@/components/pages/administration/user_roles/UserAccessHandler";
import { EnumUserPermission } from "@/app/services/generated/enums/enumUserPermission.generated";
import { showUnauthorisedDialog } from "@/components/composables/promptDialogs";
import { makeRef } from "@/components/composables/reactivityTypes";
import { screenSize } from "@/vue23switching/screenSize";
import { bmIconName } from "@/components/controls/bmIconMap";

interface RemovableColumnState {
  columnKey: string;
  headingText: string;
  removable: boolean;
  removed: boolean;
  remove: (remove: boolean) => void;
}

interface FilterSortColumnState {
  columnKey: string;
  expanded: boolean;
  toggleExpanded: () => void;
  headingText: string;
  sort: (descending: boolean) => void;
  clearSort: () => void;
  sortedAsc: boolean;
  sortedDesc: boolean;
  filterComponent?: VueConstructor | VueComponent;
}

interface BmGridControlPanelProps {
  gridState: BmGridState;
  gridConfiguration: BmGridConfiguration;
  showSearch?: boolean;
  hideFilter?: boolean;
  hideSort?: boolean;
  showManageColumns?: boolean;
  showExport?: boolean;
  customPrintTitle?: (() => string) | string | null;
  showExpandAll?: boolean;
  allRowsExpanded?: boolean;
}

interface BmGridControlPanelEvents {
  (event: "toggle-expand-all"): void;
}

const props = withDefaults(defineProps<BmGridControlPanelProps>(), {
  showSearch: true,
  hideFilter: false,
  hideSort: false,
  showManageColumns: true,
  showExport: true,
  customPrintTitle: null,
  showExpandAll: false,
  allRowsExpanded: false,
});

const emit = defineEmits<BmGridControlPanelEvents>();

const { router } = useRouter();

const searchValue = makeRef(props.gridState.gridSearchFilter ?? "");
const showColumnSelectorDrawer = makeRef(false);
const showFilterDrawer = makeRef(false);
const showExportDrawer = makeRef(false);
const shownSortAndFilterBoxes = makeRef<readonly string[]>(
  Object.entries(props.gridConfiguration.columns)
    .filter(
      ([key, column]) =>
        column.sortableAndFilterable && props.gridState.columnFilters[key]
    )
    .map(([key]) => key)
);

const sortAndOrFilterText = computed(() => {
  if (props.hideFilter) {
    return messages.grid_only_sort;
  }

  if (props.hideSort) {
    return messages.grid_only_filter;
  }

  return messages.grid_filter;
});

const sortedColumns = computed(() =>
  Object.entries(props.gridConfiguration.columns).sort(
    indexedKeyComparer(props.gridState.columnOrder, (x) => x[0])
  )
);

const activelyFilteredColumns = computed(() =>
  Object.entries(props.gridState.columnFilters)
);

const sortableAndFilterableColumns = computed((): FilterSortColumnState[] => {
  const gridState = props.gridState;
  return sortedColumns.value
    .filter(
      ([_, column]) => column.sortableAndFilterable && column.showInGridControls
    )
    .map(([key, column]) => {
      return {
        columnKey: key,
        expanded: shownSortAndFilterBoxes.value.includes(key),
        headingText: column.headingText,
        toggleExpanded: () => {
          shownSortAndFilterBoxes.value =
            shownSortAndFilterBoxes.value.includes(key)
              ? shownSortAndFilterBoxes.value.filter((k) => k !== key)
              : shownSortAndFilterBoxes.value.concat(key);
        },
        sort: (descending: boolean) => {
          // we only assign single sort criteria when originated from user interaction. They can 'stack' manually if
          // desired.
          // eslint-disable-next-line vue/no-mutating-props
          props.gridState.sortBy = [
            {
              columnKey: key,
              descending: descending,
            },
          ];
        },
        clearSort() {
          gridState.sortBy = [];
        },
        get sortedAsc(): boolean {
          return gridState.sortBy.some(
            (sortBy) =>
              sortBy.columnKey === this.columnKey && !sortBy.descending
          );
        },
        get sortedDesc(): boolean {
          return gridState.sortBy.some(
            (sortBy) => sortBy.columnKey === this.columnKey && sortBy.descending
          );
        },
        filterComponent: column.columnFilter?.columnFilterComponent,
      };
    });
});

const removableColumns = computed((): RemovableColumnState[] => {
  return sortedColumns.value
    .filter(([key, column]) => {
      return key !== "actions" && column.showInGridControls;
    })
    .map(([key, column]) => {
      return {
        columnKey: key,
        headingText: column.headingText,
        removable:
          column.removable && !props.gridState.groupBy.some((g) => g === key),
        removed: props.gridState.removedColumnKeys.some((g) => g === key),
        remove: (remove: boolean) => {
          const itemIdx = props.gridState.removedColumnKeys.indexOf(key);
          if (remove && itemIdx !== -1) {
            // eslint-disable-next-line vue/no-mutating-props
            props.gridState.removedColumnKeys.splice(itemIdx, 1);
          } else if (!remove && itemIdx === -1) {
            // eslint-disable-next-line vue/no-mutating-props
            props.gridState.removedColumnKeys.push(key);
          }
        },
      };
    });
});

const searchWatcher = debounce(() => {
  // eslint-disable-next-line vue/no-mutating-props
  props.gridState.gridSearchFilter = searchValue.value;
}, 300);

watch(searchValue, () => searchWatcher());

onUnmounted(() => {
  searchWatcher.cancel();
});

function resetAllFilters() {
  searchValue.value = "";
  showColumnSelectorDrawer.value = false;
  showFilterDrawer.value = false;
  props.gridState.reset();
}

function deleteFilter(key: string) {
  props.gridState.setFilter(key, undefined);
}

function exportGrid(format: GridExportFormat) {
  const customTitle =
    props.customPrintTitle instanceof Function
      ? props.customPrintTitle()
      : props.customPrintTitle;

  const fileTitle =
    customTitle ??
    props.gridConfiguration.title ??
    translate(Key.fromString(router.currentRoute.name + ""));

  const event: BmGridExportEvent = {
    filename: fileTitle + "." + format,
    fileTitle,
    format: format,
    stateId: props.gridState.stateId,
  };

  // Kick off the export but dont wait for it.
  activeGridRegistry.doExport(event);
}

async function exportButtonClicked() {
  const requiredPermissions = [EnumUserPermission.exportTables];
  if (hasPermissionsUsingUserStore(requiredPermissions)) {
    showExportDrawer.value = true;
  } else {
    await showUnauthorisedDialog({ requiredPermissions });
  }
}
</script>

<style scoped lang="sass">
.inline-search
  padding-top: 0

// Make the buttons compress in mobile
:deep(.v-btn)
  min-width: 32px !important
  padding: 0 8px !important

// Drawer

.manage-columns .bm-checkbox
  margin-left: 8px
  margin-top: 10px

.control-box
  margin: 10px

.control-box :deep(.v-card__title)
  padding: 8px

.control-box :deep(.v-card__text)
  padding: 5px 16px

.control-box .sort-controls hr
  margin-top: 10px

.control-box .clickable
  cursor: pointer

.grid-controls-actions
  min-width: fit-content
  display: flex
  flex-flow: row
  column-gap: 4px
  justify-content: flex-end
</style>
